import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getSocketUrl, getSocketConfig } from '@/lib/socketUtils';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Video, {
  Room, LocalVideoTrack, LocalAudioTrack, LocalDataTrack, RemoteParticipant,
  LocalParticipant, RemoteTrack, RemoteTrackPublication,
  LocalTrackPublication, createLocalVideoTrack, createLocalAudioTrack
} from '@/lib/livekit-room-adapter';
import {
  Mic, MicOff, Video as VideoIcon, VideoOff, Monitor, MonitorOff,
  PhoneOff, Settings, Users, MessageSquare, Circle, Copy, Link,
  Maximize2, Minimize2, Hand, MoreVertical, ChevronDown, Send, X,
  Wifi, WifiOff, Loader2, LayoutGrid, User, Pin, PinOff,
  Volume2, VolumeX, LogOut, UserX, FileText
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { apiClient, type ApiResponse } from '@/lib/api';
import { useSocketOptional } from '@/contexts/SocketContext';
import { useAuth } from '@/contexts/AuthContext';

interface RoomRecordingDTO {
  id: string;
  egress_id: string | null;
  // Match esatto a `video_recordings.status` (backend `video.service.ts`):
  // `processing` = egress in corso, `completed` / `failed` = terminale.
  status: 'processing' | 'completed' | 'failed';
}

interface ChatMessage {
  identity: string;
  displayName: string;
  text: string;
  timestamp: Date;
}

interface ParticipantInfo {
  identity: string;
  displayName: string;
  audioEnabled: boolean;
  videoEnabled: boolean;
  isScreenShare: boolean;
  networkQuality: number | null;
  isDominantSpeaker: boolean;
}

interface TranscriptionLine {
  speakerIdentity: string;
  speakerName: string;
  text: string;
  timestamp: Date;
}

/** Sottoinsieme di `video_rooms` restituito dal backend nel join payload. */
interface VideoRoomInfo {
  id?: string;
  uniqueName?: string;
  roomSid?: string;
  title?: string;
  roomType?: string;
  recordOnConnect?: boolean;
  admissionRequired?: boolean;
  transcription_enabled?: boolean;
  transcriptionEnabled?: boolean;
}

/** Risposta di POST /video/rooms/:id/join e /video/guest/:token/join. */
interface VideoJoinResponse {
  token?: string;
  identity?: string;
  displayName?: string;
  wsUrl?: string;
  livekitUrl?: string;
  pendingAdmission?: boolean;
  participantId?: string;
  room?: VideoRoomInfo;
}

/** Partecipante in attesa di ammissione (lista host). */
interface PendingParticipant {
  id: string;
  display_name?: string;
  identity?: string;
  status?: string;
}

const SPEAKER_COLORS = [
  'text-blue-400', 'text-green-400', 'text-purple-400',
  'text-amber-400', 'text-pink-400', 'text-cyan-400',
  'text-orange-400', 'text-emerald-400'
];

function getSpeakerColor(identity: string): string {
  let hash = 0;
  for (let i = 0; i < identity.length; i++) {
    hash = ((hash << 5) - hash) + identity.charCodeAt(i);
    hash |= 0;
  }
  return SPEAKER_COLORS[Math.abs(hash) % SPEAKER_COLORS.length] ?? '';
}

type ViewMode = 'gallery' | 'speaker';

/**
 * Bundle dei track remoti per partecipante: declarative state che
 * sostituisce il vecchio Map DOM-imperativo `remoteTrackElementsRef`.
 * Le chiavi sono mutuamente esclusive: una traccia camera (`video`),
 * una di screen-share (`video` con name='screen'), una audio.
 */
interface RemoteTrackBundle {
  video?: RemoteTrack;
  screen?: RemoteTrack;
  audio?: RemoteTrack;
}

/**
 * Feature-detect screen sharing. Mobile browsers (iOS Safari, Android
 * Chrome/Firefox) NON espongono `getDisplayMedia` o falliscono comunque
 * — nascondere il bottone evita errori utente in produzione.
 */
function canScreenShare(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return false;
  return true;
}

/**
 * Vista declarativa di un RemoteTrack video. Rende un singolo `<video>` e
 * delega l'attach/detach a livekit via effect (gestione one-shot per mount).
 * Top-level component → identità stabile, niente unmount/remount per ogni
 * re-render del parent.
 */
function RemoteVideoView({ track, contain }: { track: RemoteTrack; contain?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || track.kind !== 'video') return;
    try { track.attach(el); } catch { /* ignore */ }
    return () => { try { track.detach(el); } catch { /* ignore */ } };
  }, [track]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      style={{ width: '100%', height: '100%', objectFit: contain ? 'contain' : 'cover', borderRadius: 0 }}
    />
  );
}

function RemoteAudioView({ track }: { track: RemoteTrack }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || track.kind !== 'audio') return;
    try { track.attach(el); } catch { /* ignore */ }
    return () => { try { track.detach(el); } catch { /* ignore */ } };
  }, [track]);
  return <audio ref={ref} autoPlay playsInline style={{ display: 'none' }} />;
}

interface RemoteVideoTileProps {
  identity: string;
  remoteTracks: Map<string, RemoteTrackBundle>;
  isScreenTrack?: boolean;
}

function RemoteVideoTile({ identity, remoteTracks, isScreenTrack }: RemoteVideoTileProps) {
  const bundle = remoteTracks.get(identity);
  const track = isScreenTrack ? bundle?.screen : bundle?.video;
  if (!track) return <div className="w-full h-full" />;
  return <RemoteVideoView track={track} contain={isScreenTrack} />;
}

function LocalScreenView({ track }: { track: LocalVideoTrack }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    try {
      el.srcObject = new MediaStream([track.mediaStreamTrack]);
      el.play().catch(() => undefined);
    } catch { /* ignore */ }
    return () => { try { el.srcObject = null; } catch { /* ignore */ } };
  }, [track]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  );
}

export default function VideoCallPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  const guestToken = searchParams.get('guest');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  const [status, setStatus] = useState<'loading' | 'guest-lobby' | 'preview' | 'connecting' | 'connected' | 'ended'>('loading');
  const [room, setRoom] = useState<Room | null>(null);
  const [roomInfo, setRoomInfo] = useState<VideoRoomInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [localVideoTrack, setLocalVideoTrack] = useState<LocalVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<LocalAudioTrack | null>(null);
  const [screenTrack, setScreenTrack] = useState<LocalVideoTrack | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [participants, setParticipants] = useState<Map<string, ParticipantInfo>>(new Map());
  const [dominantSpeaker, setDominantSpeaker] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showGuestLinkModal, setShowGuestLinkModal] = useState(false);
  const [guestLink, setGuestLink] = useState('');
  const [pendingAdmission, setPendingAdmission] = useState(false);
  const [pendingParticipantId, setPendingParticipantId] = useState<string | null>(null);
  const [pendingParticipants, setPendingParticipants] = useState<PendingParticipant[]>([]);
  const [isHost, setIsHost] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('gallery');
  const [pinnedParticipant, setPinnedParticipant] = useState<string | null>(null);
  const [screenShareActive, setScreenShareActive] = useState<string | null>(null);
  const [showMobilePanel, setShowMobilePanel] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [muteRequestDialog, setMuteRequestDialog] = useState<string | null>(null);

  /**
   * Track remoti per identity. Sostituisce il vecchio
   * `remoteTrackElementsRef` (Map DOM-imperativa) con state React, così che
   * RemoteVideoView/RemoteAudioView possano gestire attach/detach
   * declarativamente nei propri effect — niente più `removeChild`/`appendChild`
   * in lotta con la reconciliation di React.
   */
  const [remoteTracks, setRemoteTracks] = useState<Map<string, RemoteTrackBundle>>(new Map());
  const screenShareSupported = useMemo(() => canScreenShare(), []);

  const [guestDisplayName, setGuestDisplayName] = useState('');

  const [soloWarning, setSoloWarning] = useState<{ countdown: number; message: string } | null>(null);
  const soloCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isTranscribing, setIsTranscribing] = useState(false);
  // Task #1474 — sorgente trascrizione: 'server' = worker server-side BE-3/BE-4
  // attivo, il client NON deve avviare MediaRecorder; 'client' = fallback locale.
  const transcriptionSourceRef = useRef<'server' | 'client'>('client');
  const [transcriptionLines, setTranscriptionLines] = useState<TranscriptionLine[]>([]);
  const [showTranscription, setShowTranscription] = useState(false);

  // Task #1458 — Recording on-demand state
  const [recordingActive, setRecordingActive] = useState(false);
  const [recordingEgressId, setRecordingEgressId] = useState<string | null>(null);
  const [recordingBusy, setRecordingBusy] = useState(false);

  const socketCtx = useSocketOptional();

  // Task #1473: socket di trascrizione unificato per utenti autenticati e
  // ospiti. Per gli utenti loggati riusiamo `socketCtx.socket` (sessione
  // condivisa). Per gli ospiti — che non hanno JWT, quindi `socketCtx` è
  // null — apriamo un socket dedicato con `videoGuestToken` validato dal
  // backend in handshake (vedi `index.ts` socket auth middleware).
  const guestSocketRef = useRef<Socket | null>(null);
  const [transcriptionSocketReady, setTranscriptionSocketReady] = useState(false);
  const transcriptionSocket: Socket | null = socketCtx?.socket ?? guestSocketRef.current;

  const localVideoContainerRef = useRef<HTMLDivElement | null>(null);
  const localVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const localVideoRef = useCallback((node: HTMLDivElement | null) => {
    localVideoContainerRef.current = node;
    if (!node) return;
    const track = localVideoTrackRef.current;
    if (!track) return;
    node.innerHTML = '';
    const el = track.attach();
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.style.borderRadius = '0px';
    node.appendChild(el);
  }, []);
  const screenShareDisplayRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dataTrackRef = useRef<LocalDataTrack | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const displayNameMapRef = useRef<Map<string, string>>(new Map());
  const myDisplayNameRef = useRef<string>('Tu');
  const myIdentityRef = useRef<string>('');
  const admissionPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const connectedRef = useRef(false);

  const roomRef = useRef<Room | null>(null);
  const localAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  const screenTrackRef = useRef<LocalVideoTrack | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunkIndexRef = useRef(0);
  const transcriptionEndRef = useRef<HTMLDivElement>(null);
  const isTranscribingRef = useRef(false);

  useEffect(() => { roomRef.current = room; }, [room]);
  useEffect(() => { localVideoTrackRef.current = localVideoTrack; }, [localVideoTrack]);
  useEffect(() => { localAudioTrackRef.current = localAudioTrack; }, [localAudioTrack]);
  useEffect(() => { screenTrackRef.current = screenTrack; }, [screenTrack]);

  const loadRoomInfo = useCallback(async () => {
    try {
      if (guestToken) {
        setStatus('guest-lobby');
        setRoomInfo({ title: 'Video Call' });
        return;
      }
      const res = await apiClient.get<{ isCreator?: boolean } & VideoRoomInfo>(`/video/rooms/${roomId}`);
      if (res.success) {
        setRoomInfo(res.data ?? null);
        // Host privileges: deve combaciare con backend `assertRoomHost`
        // (`video.service.ts`) — creator della room OPPURE ruolo tenant
        // owner/admin/moderator. NON includere super_admin lato FE: il
        // backend non lo accetta nella host gate, mostrare il bottone
        // genererebbe un 403 confuso.
        const role = (currentUser?.role || '').toLowerCase();
        const isPrivileged = role === 'owner' || role === 'admin' || role === 'moderator';
        if (res.data?.isCreator || isPrivileged) setIsHost(true);
        setStatus('preview');
      } else {
        setError(res.error || 'Room non trovata');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    }
  }, [guestToken, roomId]);

  useEffect(() => {
    loadRoomInfo();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [roomId, loadRoomInfo]);

  const admissionSocketCleanupRef = useRef<(() => void) | null>(null);

  const stopTranscription = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch { /* */ }
    }
    mediaRecorderRef.current = null;
    isTranscribingRef.current = false;
    setIsTranscribing(false);
  }, []);

  const cleanupRoom = useCallback(() => {
    if (roomRef.current) roomRef.current.disconnect();
    if (localVideoTrackRef.current) localVideoTrackRef.current.stop();
    if (localAudioTrackRef.current) localAudioTrackRef.current.stop();
    if (screenTrackRef.current) screenTrackRef.current.stop();
    if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
    if (admissionSocketCleanupRef.current) {
      admissionSocketCleanupRef.current();
      admissionSocketCleanupRef.current = null;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    if (soloCountdownRef.current) clearInterval(soloCountdownRef.current);
    stopTranscription();
    dataTrackRef.current = null;
    connectedRef.current = false;
    setRemoteTracks(new Map());
  }, [stopTranscription]);

  useEffect(() => {
    return () => {
      cleanupRoom();
    };
  }, [cleanupRoom]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Task #1473: avvio silente per host/utenti loggati/ospiti — niente
  // apertura del pannello laterale. Il pannello si apre solo su click
  // esplicito dell'utente sull'icona Trascrizione.
  const unsupportedToastShownRef = useRef(false);
  const startTranscription = useCallback(() => {
    if (isTranscribingRef.current) return;
    // Task #1474 — se il worker server-side è agganciato alla room, NON avviare
    // MediaRecorder: la trascrizione arriva da BE-3/BE-4 via Socket.IO.
    if (transcriptionSourceRef.current === 'server') {
      isTranscribingRef.current = true;
      setIsTranscribing(true);
      return;
    }
    const audioTrack = localAudioTrackRef.current;
    const sock = transcriptionSocket;
    if (!sock || !roomId || !audioTrack) {
      return;
    }

    if (typeof MediaRecorder === 'undefined') {
      if (!unsupportedToastShownRef.current) {
        unsupportedToastShownRef.current = true;
        toast({
          title: 'Trascrizione non supportata',
          description: 'Il tuo browser non supporta la trascrizione audio in tempo reale.',
          variant: 'destructive',
        });
      }
      return;
    }

    try {
      const msTrack = audioTrack.mediaStreamTrack.clone();
      const stream = new MediaStream([msTrack]);

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';

      if (!mimeType) {
        if (!unsupportedToastShownRef.current) {
          unsupportedToastShownRef.current = true;
          toast({
            title: 'Trascrizione non supportata',
            description: 'Il tuo browser non supporta il formato audio richiesto (WebM/Opus).',
            variant: 'destructive',
          });
        }
        return;
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      const guestName = guestDisplayName?.trim() || undefined;

      recorder.ondataavailable = (e) => {
        if (!e.data || e.data.size < 200 || !isTranscribingRef.current) return;

        const blob = e.data;
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string)?.split(',')[1];
          if (base64 && sock.connected) {
            sock.emit('video:transcription:chunk', {
              roomId,
              audioData: base64,
              chunkIndex: chunkIndexRef.current++,
              displayName: guestName,
            });
          }
        };
        reader.readAsDataURL(blob);
      };

      recorder.onerror = (ev: Event) => {
        const err = (ev as unknown as { error?: { message?: string } }).error;
        console.error('[Transcription] MediaRecorder error:', err?.message || ev);
      };

      recorder.start(6000);
      mediaRecorderRef.current = recorder;
      isTranscribingRef.current = true;
      setIsTranscribing(true);
    } catch (err) {
      console.error('[Transcription] Failed to start:', err);
      if (!unsupportedToastShownRef.current) {
        unsupportedToastShownRef.current = true;
        toast({
          title: 'Errore trascrizione',
          description: err instanceof Error ? err.message : 'Avvio fallito.',
          variant: 'destructive',
        });
      }
    }
  }, [transcriptionSocket, roomId, guestDisplayName, toast]);

  // Task #1473: solo l'host può abilitare/disabilitare la trascrizione di
  // room (broadcast a tutti). Il bottone resta nel pannello laterale.
  const toggleTranscription = useCallback(() => {
    const sock = transcriptionSocket;
    if (!sock || !roomId || !isHost) return;
    sock.emit('video:transcription:toggle', { roomId, enabled: !isTranscribing });
    // Nessuna apertura/chiusura forzata del pannello: l'icona in barra lo gestisce.
  }, [transcriptionSocket, roomId, isHost, isTranscribing]);

  // Task #1473: listener trascrizione + recording sul socket unificato
  // (autenticato o ospite). Gli eventi `video:transcription:{text,toggled,
  // status}` sono broadcast su `video:${roomId}` lato BE: tutti i
  // partecipanti — ospiti inclusi — li ricevono.
  useEffect(() => {
    const sock = transcriptionSocket;
    if (!sock || !roomId) return;

    type TranscriptionTextPayload = {
      roomId: string;
      speakerIdentity: string;
      speakerName: string;
      text: string;
      timestamp: string | number | Date;
    };
    type TranscriptionTogglePayload = { roomId: string; enabled: boolean; language?: string };
    type TranscriptionStatusPayload = { roomId: string; active: boolean; language?: string };

    const handleTranscriptionText = (data: TranscriptionTextPayload) => {
      if (data.roomId !== roomId) return;
      setTranscriptionLines(prev => [...prev, {
        speakerIdentity: data.speakerIdentity,
        speakerName: data.speakerName,
        text: data.text,
        timestamp: new Date(data.timestamp),
      }]);
    };

    const handleTranscriptionToggled = (data: TranscriptionTogglePayload) => {
      if (data.roomId !== roomId) return;
      if (data.enabled && !isTranscribingRef.current) {
        // Task #1473: avvio SILENZIOSO — niente apertura del pannello.
        startTranscription();
      } else if (!data.enabled && isTranscribingRef.current) {
        stopTranscription();
      }
    };

    const handleTranscriptionStatus = (data: TranscriptionStatusPayload) => {
      if (data.roomId !== roomId) return;
      if (data.active && !isTranscribingRef.current) {
        startTranscription();
      } else if (!data.active && isTranscribingRef.current) {
        stopTranscription();
      }
    };

    type TranscriptionSourcePayload = { roomId: string; source: 'server' | 'client' };
    const handleTranscriptionSource = (data: TranscriptionSourcePayload) => {
      if (data.roomId !== roomId) return;
      transcriptionSourceRef.current = data.source;
      // Se passiamo a server e il client stava registrando, fermalo (il
      // server prende il sopravvento). Se passiamo a client (fallback),
      // riavvia MediaRecorder se la trascrizione è ancora attiva.
      if (data.source === 'server' && mediaRecorderRef.current) {
        try { mediaRecorderRef.current.stop(); } catch { /* */ }
        mediaRecorderRef.current = null;
      } else if (data.source === 'client' && isTranscribingRef.current && !mediaRecorderRef.current) {
        isTranscribingRef.current = false;
        setIsTranscribing(false);
        startTranscription();
      }
    };

    sock.on('video:transcription:text', handleTranscriptionText);
    sock.on('video:transcription:toggled', handleTranscriptionToggled);
    sock.on('video:transcription:status', handleTranscriptionStatus);
    sock.on('video:transcription:source', handleTranscriptionSource);

    type RecordingPayload = { roomId: string; egressId: string };
    const onRecStarted = (payload: RecordingPayload) => {
      if (payload.roomId !== roomId) return;
      setRecordingActive(true);
      setRecordingEgressId(payload.egressId);
    };
    const onRecStopped = (payload: RecordingPayload) => {
      if (payload.roomId !== roomId) return;
      setRecordingActive(false);
      setRecordingEgressId(null);
    };
    sock.on('video:recording:started', onRecStarted);
    sock.on('video:recording:stopped', onRecStopped);

    return () => {
      sock.off('video:transcription:text', handleTranscriptionText);
      sock.off('video:transcription:toggled', handleTranscriptionToggled);
      sock.off('video:transcription:status', handleTranscriptionStatus);
      sock.off('video:transcription:source', handleTranscriptionSource);
      sock.off('video:recording:started', onRecStarted);
      sock.off('video:recording:stopped', onRecStopped);
    };
  }, [transcriptionSocket, roomId, startTranscription, stopTranscription, transcriptionSocketReady]);

  // Task #1473: ciclo di vita del socket ospite. Aperto solo in stato
  // `connected` (post-join) con `videoGuestToken`; chiuso allo smontaggio
  // o quando l'ospite lascia la call. Per gli utenti loggati questo
  // effetto è no-op (`socketCtx` esistente già copre il bisogno).
  useEffect(() => {
    if (!guestToken) return;
    if (status !== 'connected') return;
    if (guestSocketRef.current) return;

    const socketUrl = getSocketUrl();
    const baseConfig = getSocketConfig();
    const sock = io(socketUrl, {
      ...baseConfig,
      auth: { videoGuestToken: guestToken },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    sock.on('connect', () => setTranscriptionSocketReady(true));
    sock.on('disconnect', () => setTranscriptionSocketReady(false));
    sock.on('connect_error', (err: Error) => {
      console.warn('[VideoCall] Guest socket connect_error:', err.message);
    });

    guestSocketRef.current = sock;
    return () => {
      try { sock.disconnect(); } catch { /* */ }
      guestSocketRef.current = null;
      setTranscriptionSocketReady(false);
    };
  }, [guestToken, status]);

  // Task #1473: autostart locale basato su `roomInfo.transcription_enabled`.
  // Per host/utenti loggati il flag arriva da `GET /video/rooms/:id`
  // (loadRoomInfo); per gli ospiti arriva da `POST /video/guest/:token/join`
  // dentro `tokenData.room.transcriptionEnabled` (joinRoom). Triggerato
  // quando: stato connesso + audio track pronto + flag true. Idempotente
  // grazie al guard in `startTranscription` (`isTranscribingRef.current`).
  useEffect(() => {
    if (status !== 'connected') return;
    if (!localAudioTrack) return;
    if (!roomInfo) return;
    const enabled = roomInfo.transcription_enabled === true || roomInfo.transcriptionEnabled === true;
    if (enabled && !isTranscribingRef.current) {
      startTranscription();
    }
  }, [status, localAudioTrack, roomInfo, startTranscription]);

  // Task #1458 — Recording on-demand actions (host-only).
  const startRecording = useCallback(async () => {
    if (!roomId || recordingBusy) return;
    setRecordingBusy(true);
    try {
      const res = await apiClient.post(`/video/rooms/${roomId}/recording/start`, {});
      if (!res.success) throw new Error(res.error || 'Errore avvio registrazione');
      const data = (res as { data?: { compositionSid?: string } }).data;
      if (data?.compositionSid) {
        setRecordingActive(true);
        setRecordingEgressId(data.compositionSid);
      }
      toast({ title: 'Registrazione avviata' });
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Impossibile avviare', variant: 'destructive' });
    } finally {
      setRecordingBusy(false);
    }
  }, [roomId, recordingBusy, toast]);

  const stopRecording = useCallback(async () => {
    if (!roomId || !recordingEgressId || recordingBusy) return;
    setRecordingBusy(true);
    try {
      const res = await apiClient.post(`/video/rooms/${roomId}/recording/stop`, { egressId: recordingEgressId });
      if (!res.success) throw new Error(res.error || 'Errore stop registrazione');
      setRecordingActive(false);
      setRecordingEgressId(null);
      toast({ title: 'Registrazione fermata' });
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Impossibile fermare', variant: 'destructive' });
    } finally {
      setRecordingBusy(false);
    }
  }, [roomId, recordingEgressId, recordingBusy, toast]);

  useEffect(() => {
    if (!socketCtx || !roomId) return;

    const handleSoloWarning = (data: { roomId?: string; countdownSeconds?: number; message?: string }) => {
      if (data.roomId !== roomId) return;

      if (soloCountdownRef.current) clearInterval(soloCountdownRef.current);

      let remaining = data.countdownSeconds || 120;
      setSoloWarning({ countdown: remaining, message: data.message || '' });

      soloCountdownRef.current = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          if (soloCountdownRef.current) clearInterval(soloCountdownRef.current);
          setSoloWarning(null);
          if (roomRef.current) {
            roomRef.current.disconnect();
          }
          setStatus('ended');
        } else {
          setSoloWarning(prev => prev ? { ...prev, countdown: remaining } : null);
        }
      }, 1000);
    };

    socketCtx.on('video:solo-warning', handleSoloWarning);

    return () => {
      socketCtx.off('video:solo-warning', handleSoloWarning);
      if (soloCountdownRef.current) {
        clearInterval(soloCountdownRef.current);
        soloCountdownRef.current = null;
      }
    };
  }, [socketCtx, roomId]);

  useEffect(() => {
    if (transcriptionEndRef.current) {
      transcriptionEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcriptionLines]);

  const resolveDisplayName = useCallback((identity: string): string => {
    const cached = displayNameMapRef.current.get(identity);
    if (cached) return cached;
    if (identity.startsWith('guest_')) return 'Ospite';
    return identity.replace('user_', '');
  }, []);

  const broadcastIdentity = useCallback((dataTrack: LocalDataTrack, identity: string, displayName: string) => {
    try {
      dataTrack.send(JSON.stringify({ type: 'identity', identity, displayName }));
    } catch (e) {
      console.warn('[VideoCall] Failed to broadcast identity:', e);
    }
  }, []);

  const sendDataTrackMessage = useCallback((msg: Record<string, any>) => {
    const dt = dataTrackRef.current;
    if (dt) {
      try { dt.send(JSON.stringify(msg)); } catch (e) {}
    }
  }, []);

  const handleTrackSubscribed = useCallback((track: RemoteTrack, participant: RemoteParticipant) => {
    if (track.kind === 'data') {
      track.on('message', (data: string) => {
        try {
          const msg = JSON.parse(data);
          if (msg.type === 'identity' && msg.identity && msg.displayName) {
            displayNameMapRef.current.set(msg.identity, msg.displayName);
            setParticipants(prev => {
              const next = new Map(prev);
              const info = next.get(msg.identity);
              if (info) {
                next.set(msg.identity, { ...info, displayName: msg.displayName });
              }
              return next;
            });
          }
          if (msg.type === 'identity_request' && dataTrackRef.current) {
            broadcastIdentity(dataTrackRef.current, myIdentityRef.current, myDisplayNameRef.current);
          }
          if (msg.type === 'chat') {
            setChatMessages(prev => [...prev, {
              identity: participant.identity,
              displayName: msg.displayName || resolveDisplayName(participant.identity),
              text: msg.text,
              timestamp: new Date()
            }]);
          }
          if (msg.type === 'mute_request' && (msg.targetIdentity === myIdentityRef.current || msg.targetIdentity === 'all')) {
            setMuteRequestDialog(msg.senderName || 'L\'host');
          }
        } catch (e) {}
      });

      sendDataTrackMessage({ type: 'identity_request' });
      return;
    }

    if (track.kind === 'video') {
      const isScreen = track.name === 'screen';
      if (isScreen) {
        setScreenShareActive(participant.identity);
        setParticipants(prev => {
          const next = new Map(prev);
          const info = next.get(participant.identity);
          if (info) next.set(participant.identity, { ...info, isScreenShare: true });
          return next;
        });
      }

      setRemoteTracks(prev => {
        const next = new Map(prev);
        const cur = next.get(participant.identity) || {};
        next.set(participant.identity, isScreen ? { ...cur, screen: track } : { ...cur, video: track });
        return next;
      });
      return;
    }

    if (track.kind === 'audio') {
      setRemoteTracks(prev => {
        const next = new Map(prev);
        const cur = next.get(participant.identity) || {};
        next.set(participant.identity, { ...cur, audio: track });
        return next;
      });
    }
  }, [resolveDisplayName, broadcastIdentity, sendDataTrackMessage]);

  const handleTrackUnsubscribed = useCallback((track: RemoteTrack, participant: RemoteParticipant) => {
    const isScreen = track.kind === 'video' && track.name === 'screen';
    if (isScreen) {
      setScreenShareActive(prev => prev === participant.identity ? null : prev);
      setParticipants(prev => {
        const next = new Map(prev);
        const info = next.get(participant.identity);
        if (info) next.set(participant.identity, { ...info, isScreenShare: false });
        return next;
      });
    }
    setRemoteTracks(prev => {
      const next = new Map(prev);
      const cur = next.get(participant.identity);
      if (!cur) return prev;
      const updated: RemoteTrackBundle = { ...cur };
      if (track.kind === 'video') {
        if (isScreen) delete updated.screen;
        else delete updated.video;
      } else if (track.kind === 'audio') {
        delete updated.audio;
      }
      if (!updated.video && !updated.screen && !updated.audio) next.delete(participant.identity);
      else next.set(participant.identity, updated);
      return next;
    });
  }, []);

  const handleTrackEnabled = useCallback((pub: RemoteTrackPublication, participant: RemoteParticipant) => {
    setParticipants(prev => {
      const next = new Map(prev);
      const info = next.get(participant.identity);
      if (info) {
        if (pub.kind === 'audio') next.set(participant.identity, { ...info, audioEnabled: true });
        if (pub.kind === 'video') next.set(participant.identity, { ...info, videoEnabled: true });
      }
      return next;
    });
  }, []);

  const handleTrackDisabled = useCallback((pub: RemoteTrackPublication, participant: RemoteParticipant) => {
    setParticipants(prev => {
      const next = new Map(prev);
      const info = next.get(participant.identity);
      if (info) {
        if (pub.kind === 'audio') next.set(participant.identity, { ...info, audioEnabled: false });
        if (pub.kind === 'video') next.set(participant.identity, { ...info, videoEnabled: false });
      }
      return next;
    });
  }, []);

  const handleParticipantConnected = useCallback((participant: RemoteParticipant) => {
    setParticipants(prev => {
      const next = new Map(prev);
      next.set(participant.identity, {
        identity: participant.identity,
        displayName: resolveDisplayName(participant.identity),
        audioEnabled: true,
        videoEnabled: true,
        isScreenShare: false,
        networkQuality: null,
        isDominantSpeaker: false
      });
      return next;
    });

    if (dataTrackRef.current && myDisplayNameRef.current) {
      setTimeout(() => {
        if (dataTrackRef.current) {
          broadcastIdentity(dataTrackRef.current, myIdentityRef.current, myDisplayNameRef.current);
        }
      }, 500);
    }

    participant.tracks.forEach(pub => {
      if (pub.isSubscribed && pub.track) {
        handleTrackSubscribed(pub.track, participant);
      }
    });

    participant.on('trackSubscribed', (track) => handleTrackSubscribed(track, participant));
    participant.on('trackUnsubscribed', (track) => handleTrackUnsubscribed(track, participant));
    participant.on('trackEnabled', (pub) => handleTrackEnabled(pub, participant));
    participant.on('trackDisabled', (pub) => handleTrackDisabled(pub, participant));
    participant.on('networkQualityLevelChanged', (level) => {
      setParticipants(prev => {
        const next = new Map(prev);
        const info = next.get(participant.identity);
        if (info) next.set(participant.identity, { ...info, networkQuality: level });
        return next;
      });
    });
  }, [resolveDisplayName, broadcastIdentity, handleTrackDisabled, handleTrackEnabled, handleTrackSubscribed, handleTrackUnsubscribed]);

  const handleParticipantDisconnected = useCallback((participant: RemoteParticipant) => {
    setParticipants(prev => {
      const next = new Map(prev);
      next.delete(participant.identity);
      return next;
    });
    setScreenShareActive(prev => prev === participant.identity ? null : prev);
    setPinnedParticipant(prev => prev === participant.identity ? null : prev);
    setRemoteTracks(prev => {
      if (!prev.has(participant.identity)) return prev;
      const next = new Map(prev);
      next.delete(participant.identity);
      return next;
    });
  }, []);

  const fetchAndMapParticipantNames = useCallback(async (currentRoomId: string) => {
    try {
      const res = await apiClient.get<{ participants?: Array<{ display_name?: string; user_name?: string; identity?: string }> }>(`/video/rooms/${currentRoomId}`);
      if (res.success && res.data?.participants) {
        for (const p of res.data.participants) {
          const name = p.display_name || p.user_name || p.identity;
          if (name && p.identity) {
            displayNameMapRef.current.set(p.identity, name);
          }
        }
        setParticipants(prev => {
          const next = new Map(prev);
          for (const [key, info] of next) {
            const mappedName = displayNameMapRef.current.get(key);
            if (mappedName && mappedName !== info.displayName) {
              next.set(key, { ...info, displayName: mappedName });
            }
          }
          return next;
        });
      }
    } catch (e) {
      console.warn('[VideoCall] Failed to fetch participant names:', e);
    }
  }, []);

  const setupRoom = useCallback((connectedRoom: Room, tokenData: VideoJoinResponse) => {
    setRoom(connectedRoom);
    setStatus('connected');
    timerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);

    if (socketCtx?.socket && socketCtx.isConnected && roomId) {
      socketCtx.socket.emit('joinRoom', `video:${roomId}`);
    }

    // Task #1458 — Bootstrap recording state per late-joiners e refresh host:
    // backend `video_recordings.status` ha 3 valori canonici — `processing`
    // (egress in corso), `completed`, `failed` (vedi `video.service.ts`
    // ~L1184). Una recording è "attiva" sse status='processing'. Popoliamo
    // UI + `recordingEgressId` così che REC badge e (per host/owner/admin/
    // moderator) bottone Stop riflettano lo stato corrente anche per chi
    // entra dopo lo start o ricarica la tab.
    if (roomId) {
      apiClient.get<RoomRecordingDTO[]>(`/video/rooms/${roomId}/recordings`).then((res) => {
        if (!res.success) return;
        const list = (res.data as RoomRecordingDTO[] | undefined) || [];
        const active = list.find((r) => r.status === 'processing');
        if (active && active.egress_id) {
          setRecordingActive(true);
          setRecordingEgressId(active.egress_id);
        }
      }).catch(() => undefined);
    }

    connectedRoom.participants.forEach(p => handleParticipantConnected(p));
    connectedRoom.on('participantConnected', handleParticipantConnected);
    connectedRoom.on('participantDisconnected', handleParticipantDisconnected);
    connectedRoom.on('dominantSpeakerChanged', (participant) => {
      setDominantSpeaker(participant?.identity || null);
    });
    connectedRoom.on('disconnected', () => {
      setStatus('ended');
      if (timerRef.current) clearInterval(timerRef.current);
    });

    if (!guestToken && roomId) {
      fetchAndMapParticipantNames(roomId);
    }
  }, [guestToken, roomId, fetchAndMapParticipantNames, socketCtx, handleParticipantConnected, handleParticipantDisconnected]);

  const handleAdmissionJoin = useCallback(async () => {
    if (connectedRef.current) return;
    try {
      let res: ApiResponse<VideoJoinResponse>;
      if (guestToken) {
        res = await apiClient.post<{ token?: string; identity?: string; displayName?: string; wsUrl?: string; livekitUrl?: string; pendingAdmission?: boolean; participantId?: string; room?: VideoRoomInfo }>(`/video/guest/${guestToken}/join`, { guestName: guestDisplayName || undefined });
      } else {
        res = await apiClient.post<{ token?: string; identity?: string; displayName?: string; wsUrl?: string; livekitUrl?: string; pendingAdmission?: boolean; participantId?: string; room?: VideoRoomInfo }>(`/video/rooms/${roomId}/join`, {});
      }
      if (res.success && res.data) {
        const data = res.data;
        if (!data.pendingAdmission && data.token) {
          if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
          if (connectedRef.current) return;
          connectedRef.current = true;
          setPendingAdmission(false);
          setStatus('connecting');

          const identity = data.identity ?? '';
          if (data.displayName) {
            myDisplayNameRef.current = data.displayName;
            displayNameMapRef.current.set(identity, data.displayName);
          }
          myIdentityRef.current = identity;

          const tracks: (LocalAudioTrack | LocalVideoTrack)[] = [];
          if (localAudioTrackRef.current) tracks.push(localAudioTrackRef.current);
          if (localVideoTrackRef.current) tracks.push(localVideoTrackRef.current);

          const connectedRoom = await Video.connect(data.token, {
            name: data.room?.uniqueName,
            wsUrl: data.wsUrl || data.livekitUrl,
            tracks,
            dominantSpeaker: true,
            networkQuality: { local: 1, remote: 1 },
            preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
            bandwidthProfile: { video: { mode: 'collaboration', dominantSpeakerPriority: 'high', trackSwitchOffMode: 'predicted', contentPreferencesMode: 'auto', clientTrackSwitchOffControl: 'auto' } }
          });

          const localDataTrack = new LocalDataTrack();
          connectedRoom.localParticipant.publishTrack(localDataTrack);
          dataTrackRef.current = localDataTrack;
          setTimeout(() => {
            try { localDataTrack.send(JSON.stringify({ type: 'identity', identity, displayName: data.displayName || 'Utente' })); } catch (e) {}
          }, 1000);
          setupRoom(connectedRoom, data);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('rifiutata')) {
        if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
        setPendingAdmission(false);
        setError('La tua richiesta di accesso è stata rifiutata dall\'host');
        setStatus('loading');
      }
    }
  }, [guestToken, roomId, guestDisplayName, setupRoom]);

  const startAdmissionPolling = useCallback(() => {
    if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
    if (admissionSocketCleanupRef.current) {
      admissionSocketCleanupRef.current();
      admissionSocketCleanupRef.current = null;
    }

    const sock = socketCtx?.socket;
    if (sock && socketCtx?.isConnected && roomId) {
      sock.emit('joinRoom', `video:${roomId}`);
      const onResolved = (data: { roomId?: string; status?: string }) => {
        if (data?.roomId !== roomId) return;
        if (data?.status === 'admitted') {
          handleAdmissionJoin();
        } else if (data?.status === 'rejected') {
          if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
          setPendingAdmission(false);
          setError('La tua richiesta di accesso è stata rifiutata dall\'host');
          setStatus('loading');
        }
      };
      sock.on('video:admission:resolved', onResolved);
      admissionSocketCleanupRef.current = () => {
        sock.off('video:admission:resolved', onResolved);
      };
      admissionPollingRef.current = setInterval(() => {
        if (connectedRef.current) {
          if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
          return;
        }
        handleAdmissionJoin();
      }, 15000);
      return;
    }

    admissionPollingRef.current = setInterval(async () => {
      if (connectedRef.current) {
        if (admissionPollingRef.current) clearInterval(admissionPollingRef.current);
        return;
      }
      handleAdmissionJoin();
    }, 3000);
  }, [roomId, socketCtx?.socket, socketCtx?.isConnected, handleAdmissionJoin]);

  const attachLocalVideo = useCallback((track: LocalVideoTrack) => {
    const container = localVideoContainerRef.current;
    if (!container) return;
    container.innerHTML = '';
    const el = track.attach();
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.style.borderRadius = '0px';
    container.appendChild(el);
  }, []);

  const initPreview = useCallback(async () => {
    try {
      const videoTrack = await createLocalVideoTrack({ width: 640, height: 480, facingMode: 'user' });
      setLocalVideoTrack(videoTrack);
      localVideoTrackRef.current = videoTrack;
      attachLocalVideo(videoTrack);

      const audioTrack = await createLocalAudioTrack();
      setLocalAudioTrack(audioTrack);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn('[VideoCall] Preview init error:', msg);
    }
  }, [attachLocalVideo]);

  useEffect(() => {
    if (status === 'preview' || status === 'guest-lobby') {
      initPreview();
    }
  }, [status, initPreview]);

  useEffect(() => {
    if (status === 'connected' && localVideoTrack) {
      localVideoTrackRef.current = localVideoTrack;
      attachLocalVideo(localVideoTrack);
    }
  }, [status, localVideoTrack, attachLocalVideo]);

  const loadPendingParticipants = useCallback(async () => {
    if (!isHost || !roomId) return;
    try {
      const res = await apiClient.get<{ participants?: PendingParticipant[] }>(`/video/rooms/${roomId}`);
      if (res.success && res.data?.participants) {
        const pending = res.data.participants.filter((p) => p.status === 'pending_admission');
        setPendingParticipants(pending);
      }
    } catch (e) {}
  }, [isHost, roomId]);

  useEffect(() => {
    if (!isHost || status !== 'connected') return;
    loadPendingParticipants();

    const sock = socketCtx?.socket;
    if (sock && socketCtx?.isConnected) {
      sock.emit('joinRoom', `video:${roomId}`);
      const onAdmissionReq = (data: { roomId?: string; participantId?: string; displayName?: string }) => {
        if (data?.roomId === roomId) {
          setPendingParticipants(prev => {
            if (prev.some((p: PendingParticipant) => p.id === data.participantId)) return prev;
            return [...prev, { id: data.participantId ?? '', display_name: data.displayName, status: 'pending_admission' }];
          });
        }
      };
      sock.on('video:admission:request', onAdmissionReq);
      const fallback = setInterval(loadPendingParticipants, 30000);
      return () => {
        sock.off('video:admission:request', onAdmissionReq);
        clearInterval(fallback);
      };
    }

    const interval = setInterval(loadPendingParticipants, 5000);
    return () => clearInterval(interval);
  }, [isHost, status, loadPendingParticipants, roomId, socketCtx?.socket, socketCtx?.isConnected]);

  const admitParticipant = async (participantId: string) => {
    try {
      await apiClient.post(`/video/rooms/${roomId}/admit/${participantId}`, {});
      setPendingParticipants(prev => prev.filter(p => p.id !== participantId));
      toast({ title: 'Partecipante ammesso' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const rejectParticipant = async (participantId: string) => {
    try {
      await apiClient.post(`/video/rooms/${roomId}/reject/${participantId}`, {});
      setPendingParticipants(prev => prev.filter(p => p.id !== participantId));
      toast({ title: 'Partecipante rifiutato' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const joinRoom = async () => {
    if (connectedRef.current) return;
    setStatus('connecting');
    try {
      let tokenData: VideoJoinResponse | undefined;

      if (guestToken) {
        const res = await apiClient.post<{ token?: string; identity?: string; displayName?: string; wsUrl?: string; livekitUrl?: string; pendingAdmission?: boolean; participantId?: string; room?: VideoRoomInfo }>(`/video/guest/${guestToken}/join`, { guestName: guestDisplayName || undefined });
        if (!res.success) throw new Error(res.error);
        tokenData = res.data;
      } else {
        const res = await apiClient.post<{ token?: string; identity?: string; displayName?: string; wsUrl?: string; livekitUrl?: string; pendingAdmission?: boolean; participantId?: string; room?: VideoRoomInfo }>(`/video/rooms/${roomId}/join`, {});
        if (!res.success) throw new Error(res.error);
        tokenData = res.data;
      }

      if (!tokenData) throw new Error('Risposta join non valida');

      const room = tokenData.room;
      if (room?.title) {
        setRoomInfo((prev) => ({ ...prev, ...room }));
      }

      if (tokenData.pendingAdmission) {
        setPendingAdmission(true);
        setPendingParticipantId(tokenData.participantId ?? null);
        setStatus('preview');
        startAdmissionPolling();
        return;
      }

      const identity = tokenData.identity ?? '';
      const displayName = tokenData.displayName;
      if (displayName) {
        myDisplayNameRef.current = displayName;
        displayNameMapRef.current.set(identity, displayName);
      }
      myIdentityRef.current = identity;
      connectedRef.current = true;

      const tracks: (LocalAudioTrack | LocalVideoTrack)[] = [];
      if (localAudioTrack) tracks.push(localAudioTrack);
      if (localVideoTrack) tracks.push(localVideoTrack);

      const connectedRoom = await Video.connect(tokenData.token ?? '', {
        name: room?.uniqueName,
        wsUrl: tokenData.wsUrl || tokenData.livekitUrl,
        tracks,
        dominantSpeaker: true,
        networkQuality: { local: 1, remote: 1 },
        preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
        bandwidthProfile: {
          video: {
            mode: 'collaboration',
            dominantSpeakerPriority: 'high',
            trackSwitchOffMode: 'predicted',
            contentPreferencesMode: 'auto',
            clientTrackSwitchOffControl: 'auto'
          }
        }
      });

      const localDataTrack = new LocalDataTrack();
      connectedRoom.localParticipant.publishTrack(localDataTrack);
      dataTrackRef.current = localDataTrack;

      setTimeout(() => {
        broadcastIdentity(localDataTrack, identity, displayName || 'Utente');
      }, 1000);

      setupRoom(connectedRoom, tokenData);
    } catch (e) {
      console.error('[VideoCall] Join error:', e);
      connectedRef.current = false;
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setStatus('preview');
      toast({ title: 'Errore connessione', description: msg, variant: 'destructive' });
    }
  };

  const toggleAudio = () => {
    if (localAudioTrack) {
      if (isAudioEnabled) {
        localAudioTrack.disable();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.pause();
        }
      } else {
        localAudioTrack.enable();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
          mediaRecorderRef.current.resume();
        }
      }
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  const toggleVideo = () => {
    if (localVideoTrack) {
      if (isVideoEnabled) localVideoTrack.disable();
      else localVideoTrack.enable();
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const safeUnpublish = useCallback((track: LocalVideoTrack | LocalAudioTrack) => {
    const r = roomRef.current;
    if (!r || r.state !== 'connected') return;
    try { r.localParticipant.unpublishTrack(track); } catch { /* swallow: pc closed */ }
  }, []);

  const toggleScreenShare = async () => {
    if (!screenShareSupported) {
      toast({
        title: 'Condivisione schermo non disponibile',
        description: 'Il tuo dispositivo o browser non supporta la condivisione dello schermo.',
        variant: 'destructive',
      });
      return;
    }

    if (isScreenSharing && screenTrack) {
      safeUnpublish(screenTrack);
      screenTrack.stop();
      setScreenTrack(null);
      setIsScreenSharing(false);
      setScreenShareActive(prev => prev === myIdentityRef.current ? null : prev);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const firstVideoTrack = stream.getVideoTracks()[0];
      if (!firstVideoTrack) {
        throw new Error('Nessuna traccia video disponibile per la condivisione');
      }
      const track = new LocalVideoTrack(firstVideoTrack, { name: 'screen' });

      track.mediaStreamTrack.onended = () => {
        // Guard `room.state`: durante teardown la peer connection è già
        // chiusa e `unpublishTrack` provoca `createOffer with closed peer`.
        safeUnpublish(track);
        try { track.stop(); } catch { /* */ }
        setScreenTrack(null);
        setIsScreenSharing(false);
        setScreenShareActive(prev => prev === myIdentityRef.current ? null : prev);
      };

      const r = roomRef.current;
      if (r && r.state === 'connected') {
        r.localParticipant.publishTrack(track);
      }
      setScreenTrack(track);
      setIsScreenSharing(true);
      setScreenShareActive(myIdentityRef.current);
    } catch (e) {
      const errName = e instanceof Error ? e.name : '';
      const msg = e instanceof Error ? e.message : String(e);
      // L'utente che annulla il dialog di scelta sorgente non è un errore.
      if (errName === 'NotAllowedError' || errName === 'AbortError') return;
      toast({ title: 'Errore condivisione schermo', description: msg, variant: 'destructive' });
    }
  };

  const sendChatMessage = () => {
    if (!chatInput.trim() || !room) return;

    const localDataTrack = dataTrackRef.current ||
      Array.from(room.localParticipant.dataTracks.values())
        .find(pub => pub.track)?.track;

    if (localDataTrack) {
      localDataTrack.send(JSON.stringify({
        type: 'chat',
        text: chatInput.trim(),
        displayName: myDisplayNameRef.current
      }));
    }

    setChatMessages(prev => [...prev, {
      identity: room.localParticipant.identity,
      displayName: 'Tu',
      text: chatInput.trim(),
      timestamp: new Date()
    }]);
    setChatInput('');
  };

  const leaveRoom = async () => {
    // Stop dei track PRIMA del disconnect: evita unpublishTrack su pc chiusa.
    if (localVideoTrack) { try { localVideoTrack.stop(); } catch { /* */ } }
    if (localAudioTrack) { try { localAudioTrack.stop(); } catch { /* */ } }
    if (screenTrack) { try { screenTrack.stop(); } catch { /* */ } }
    if (room) room.disconnect();
    if (timerRef.current) clearInterval(timerRef.current);
    dataTrackRef.current = null;
    connectedRef.current = false;
    setRemoteTracks(new Map());
    setStatus('ended');
  };

  const endRoomForAll = async () => {
    try {
      await apiClient.post(`/video/rooms/${roomId}/end`, {});
      leaveRoom();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const generateGuestLink = async () => {
    try {
      const res = await apiClient.post<{ link?: string }>(`/video/rooms/${roomId}/guest-link`, {});
      if (res.success) {
        setGuestLink(res.data?.link ?? '');
        setShowGuestLinkModal(true);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const requestMuteParticipant = (targetIdentity: string) => {
    sendDataTrackMessage({ type: 'mute_request', targetIdentity, senderName: myDisplayNameRef.current });
  };

  const requestMuteAll = () => {
    sendDataTrackMessage({ type: 'mute_request', targetIdentity: 'all', senderName: myDisplayNameRef.current });
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getNetworkQualityIcon = (level: number | null) => {
    if (level === null) return <Wifi className="w-3 h-3 text-gray-400" />;
    if (level >= 4) return <Wifi className="w-3 h-3 text-green-400" />;
    if (level >= 2) return <Wifi className="w-3 h-3 text-yellow-400" />;
    return <WifiOff className="w-3 h-3 text-red-400" />;
  };

  const focusedParticipant = useMemo(() => {
    if (screenShareActive) return screenShareActive;
    if (pinnedParticipant) return pinnedParticipant;
    if (viewMode === 'speaker' && dominantSpeaker) return dominantSpeaker;
    return null;
  }, [screenShareActive, pinnedParticipant, viewMode, dominantSpeaker]);

  const isSpeakerLayout = viewMode === 'speaker' || !!screenShareActive || !!pinnedParticipant;

  const VideoTile = useCallback(({ info, isLocal, isFocused, onPin }: {
    info: ParticipantInfo | null;
    isLocal: boolean;
    isFocused?: boolean;
    onPin?: () => void;
  }) => {
    const identity = info?.identity || '';
    const name = isLocal ? (myDisplayNameRef.current === 'Tu' ? 'Tu' : myDisplayNameRef.current) : (info?.displayName || 'Ospite');
    const audioOn = isLocal ? isAudioEnabled : (info?.audioEnabled ?? true);
    const videoOn = isLocal ? isVideoEnabled : (info?.videoEnabled ?? true);
    const isDominant = !isLocal && dominantSpeaker === identity;

    return (
      <div className={`relative bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center ${
        isDominant ? 'ring-2 ring-indigo-500' : ''
      } ${isFocused ? 'col-span-full row-span-full' : ''}`}
      style={{ minHeight: isFocused ? '300px' : '120px' }}
      >
        {isLocal ? (
          <>
            <div ref={localVideoRef} className="w-full h-full absolute inset-0" />
            {!isVideoEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="w-16 h-16 rounded-full bg-indigo-600 flex items-center justify-center text-2xl font-bold text-white">
                  {name.slice(0, 2).toUpperCase()}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {videoOn ? (
              <RemoteVideoTile identity={identity} remoteTracks={remoteTracks} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-700 flex items-center justify-center text-lg sm:text-2xl font-bold text-white">
                  {name.slice(0, 2).toUpperCase()}
                </div>
              </div>
            )}
          </>
        )}

        <div className="absolute bottom-1.5 left-1.5 z-10 flex items-center gap-1 bg-black/60 text-white text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg">
          {!audioOn && <MicOff className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-400" />}
          {!isLocal && info?.networkQuality !== undefined && getNetworkQualityIcon(info?.networkQuality ?? null)}
          <span className="truncate max-w-[80px] sm:max-w-[120px]">{name}</span>
        </div>

        {!isLocal && onPin && (
          <button
            onClick={(e) => { e.stopPropagation(); onPin(); }}
            className="absolute top-1.5 right-1.5 z-10 bg-black/40 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {pinnedParticipant === identity ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
          </button>
        )}
      </div>
    );
  }, [isAudioEnabled, isVideoEnabled, dominantSpeaker, pinnedParticipant, remoteTracks]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950 text-white">
        <div className="text-center space-y-4 px-4">
          <VideoOff className="w-16 h-16 text-red-400 mx-auto" />
          <h2 className="text-xl font-semibold">Impossibile accedere alla video call</h2>
          <p className="text-gray-400">{error}</p>
          <Button variant="unstyled" onClick={() => navigate(-1)} className="bg-transparent border border-white/20 text-white hover:bg-white/10">Torna indietro</Button>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950 text-white">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (status === 'ended') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950 text-white">
        <div className="text-center space-y-4 px-4">
          <PhoneOff className="w-16 h-16 text-gray-400 mx-auto" />
          <h2 className="text-xl font-semibold">Chiamata terminata</h2>
          <p className="text-gray-400">Durata: {formatDuration(callDuration)}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="unstyled" onClick={() => navigate(-1)} className="bg-transparent border border-white/20 text-white hover:bg-white/10">Torna indietro</Button>
            <Button variant="unstyled" onClick={() => { setStatus(guestToken ? 'guest-lobby' : 'preview'); setCallDuration(0); connectedRef.current = false; }} className="bg-indigo-600 hover:bg-indigo-700 text-white">Riconnetti</Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'guest-lobby') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950 text-white">
        <div className="max-w-lg w-full mx-4 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{roomInfo?.title || 'Video Call'}</h1>
            <p className="text-gray-400">Inserisci il tuo nome e controlla audio/video</p>
          </div>

          <div className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden">
            <div ref={localVideoRef} className="w-full h-full" />
            {!isVideoEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <VideoOff className="w-12 h-12 text-gray-500" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Il tuo nome *</label>
            <Input
              value={guestDisplayName}
              onChange={e => setGuestDisplayName(e.target.value)}
              placeholder="Inserisci il tuo nome"
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
              autoFocus
            />
          </div>

          <div className="flex justify-center gap-6">
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                className={`rounded-full w-14 h-14 flex items-center justify-center transition-colors ${isAudioEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                onClick={toggleAudio}
              >
                {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>
              <span className="text-xs text-gray-400">{isAudioEnabled ? 'Microfono' : 'Mutato'}</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                className={`rounded-full w-14 h-14 flex items-center justify-center transition-colors ${isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                onClick={toggleVideo}
              >
                {isVideoEnabled ? <VideoIcon className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
              <span className="text-xs text-gray-400">{isVideoEnabled ? 'Camera' : 'Camera off'}</span>
            </div>
          </div>

          <div className="flex justify-center gap-3">
            <Button variant="unstyled" onClick={() => navigate(-1)} className="bg-transparent text-white/70 hover:text-white hover:bg-white/10">Annulla</Button>
            <Button
              variant="unstyled"
              onClick={() => { setStatus('preview'); }}
              disabled={!guestDisplayName.trim()}
              className="px-8 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continua
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'preview') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950 text-white">
        <div className="max-w-lg w-full mx-4 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{roomInfo?.title || 'Video Call'}</h1>
            <p className="text-gray-400">
              {guestToken && guestDisplayName ? `Entrerai come: ${guestDisplayName}` : 'Controlla audio e video prima di entrare'}
            </p>
          </div>

          <div className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden">
            <div ref={localVideoRef} className="w-full h-full" />
            {!isVideoEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <VideoOff className="w-12 h-12 text-gray-500" />
              </div>
            )}
          </div>

          <div className="flex justify-center gap-6">
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                className={`rounded-full w-14 h-14 flex items-center justify-center transition-colors ${isAudioEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                onClick={toggleAudio}
              >
                {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>
              <span className="text-xs text-gray-400">{isAudioEnabled ? 'Microfono' : 'Mutato'}</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                className={`rounded-full w-14 h-14 flex items-center justify-center transition-colors ${isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                onClick={toggleVideo}
              >
                {isVideoEnabled ? <VideoIcon className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
              <span className="text-xs text-gray-400">{isVideoEnabled ? 'Camera' : 'Camera off'}</span>
            </div>
          </div>

          {pendingAdmission ? (
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                <p className="text-amber-300 font-medium">In attesa di ammissione dall'host...</p>
              </div>
              <p className="text-gray-500 text-sm">L'host della riunione deve approvare la tua richiesta di accesso</p>
              <Button variant="unstyled" onClick={() => { setPendingAdmission(false); if (admissionPollingRef.current) clearInterval(admissionPollingRef.current); navigate(-1); }} className="bg-transparent text-white/70 hover:text-white hover:bg-white/10">Annulla</Button>
            </div>
          ) : (
            <div className="flex justify-center gap-3">
              <Button variant="unstyled" onClick={() => navigate(-1)} className="bg-transparent text-white/70 hover:text-white hover:bg-white/10">Annulla</Button>
              <Button variant="unstyled" onClick={joinRoom} className="px-8 bg-green-600 hover:bg-green-700 text-white">
                Entra nella call
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === 'connecting') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950 text-white">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mx-auto" />
          <p className="text-gray-400">Connessione in corso...</p>
        </div>
      </div>
    );
  }

  const participantArray = Array.from(participants.values());
  const totalCount = participantArray.length + 1;
  const sidebarOpen = showChat || showParticipants || showTranscription;

  const getGalleryGridClass = () => {
    if (totalCount === 1) return 'grid-cols-1';
    if (totalCount === 2) return 'grid-cols-1 sm:grid-cols-2';
    if (totalCount <= 4) return 'grid-cols-2';
    if (totalCount <= 6) return 'grid-cols-2 md:grid-cols-3';
    if (totalCount <= 9) return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-3';
    return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
  };

  /**
   * SidePanel come JSX-expression (NON componente locale) per evitare che
   * React re-mounti l'intero subtree ad ogni render del parent — cosa che
   * faceva perdere il focus all'<Input> della chat ad ogni keystroke.
   * Restando expression, gli elementi sono solo riconciliati, mai unmount.
   */
  const sidePanelContent = (
    <>
      <div className="flex border-b border-gray-800">
        <button
          className={`flex-1 py-2.5 text-xs font-medium ${showChat ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-400'}`}
          onClick={() => { setShowChat(true); setShowParticipants(false); setShowTranscription(false); }}
        >
          <MessageSquare className="w-3.5 h-3.5 inline mr-1" />Chat
        </button>
        <button
          className={`flex-1 py-2.5 text-xs font-medium ${showParticipants ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-400'}`}
          onClick={() => { setShowParticipants(true); setShowChat(false); setShowTranscription(false); }}
        >
          <Users className="w-3.5 h-3.5 inline mr-1" />Partecipanti ({totalCount})
        </button>
        <button
          className={`flex-1 py-2.5 text-xs font-medium ${showTranscription ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-400'}`}
          onClick={() => { setShowTranscription(true); setShowChat(false); setShowParticipants(false); }}
        >
          <FileText className="w-3.5 h-3.5 inline mr-1" />Trascrizione
        </button>
      </div>

      {showChat && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chatMessages.length === 0 && (
              <p className="text-center text-gray-500 text-xs mt-8">Nessun messaggio</p>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`text-xs ${msg.identity === room?.localParticipant.identity ? 'text-right' : ''}`}>
                <span className="text-indigo-400 font-medium">{msg.displayName}</span>
                <p className={`mt-0.5 px-2.5 py-1.5 rounded-lg inline-block max-w-[85%] ${
                  msg.identity === room?.localParticipant.identity ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-200'
                }`}>{msg.text}</p>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="p-2 border-t border-gray-800 flex gap-2">
            <Input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
              placeholder="Scrivi un messaggio..."
              className="h-8 text-xs bg-gray-800 border-gray-700 text-white"
            />
            <Button variant="unstyled" size="sm" className="h-8 px-2 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={sendChatMessage}>
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {showParticipants && (
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {isHost && pendingParticipants.length > 0 && (
            <div className="mb-3 space-y-1">
              <p className="text-xs text-amber-400 font-medium px-2">In attesa di ammissione ({pendingParticipants.length})</p>
              {pendingParticipants.map(p => (
                <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-amber-900/20 border border-amber-800/30">
                  <div className="w-7 h-7 rounded-full bg-amber-700 flex items-center justify-center text-xs font-bold">
                    {(p.display_name || 'O').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{p.display_name || p.identity}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="unstyled" size="sm" className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700 text-white" onClick={() => admitParticipant(p.id)}>Ammetti</Button>
                    <Button variant="unstyled" size="sm" className="h-6 text-[10px] px-2 bg-red-600 hover:bg-red-700 text-white" onClick={() => rejectParticipant(p.id)}>Rifiuta</Button>
                  </div>
                </div>
              ))}
              <div className="border-b border-gray-800 my-2" />
            </div>
          )}
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-800/50">
            <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-bold">Tu</div>
            <div className="flex-1">
              <p className="text-xs font-medium">Tu {isHost ? '(Host)' : ''}</p>
            </div>
            <div className="flex items-center gap-1">
              {isAudioEnabled ? <Mic className="w-3 h-3 text-green-400" /> : <MicOff className="w-3 h-3 text-red-400" />}
              {isVideoEnabled ? <VideoIcon className="w-3 h-3 text-green-400" /> : <VideoOff className="w-3 h-3 text-red-400" />}
            </div>
          </div>

          {participantArray.map(p => (
            <div key={p.identity} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800/50 group">
              <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold">
                {p.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{p.displayName}</p>
              </div>
              <div className="flex items-center gap-1">
                {getNetworkQualityIcon(p.networkQuality)}
                {p.audioEnabled ? <Mic className="w-3 h-3 text-green-400" /> : <MicOff className="w-3 h-3 text-red-400" />}
                {p.videoEnabled ? <VideoIcon className="w-3 h-3 text-green-400" /> : <VideoOff className="w-3 h-3 text-red-400" />}
                {isHost && (
                  <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                    <button
                      onClick={() => requestMuteParticipant(p.identity)}
                      className="p-0.5 rounded hover:bg-gray-700"
                      title="Richiedi mute"
                    >
                      <VolumeX className="w-3 h-3 text-gray-400 hover:text-amber-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {isHost && participantArray.length > 0 && (
            <div className="pt-2 border-t border-gray-800 mt-2">
              <Button
                variant="unstyled"
                size="sm"
                className="w-full h-7 text-[10px] bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-700/30"
                onClick={requestMuteAll}
              >
                <VolumeX className="w-3 h-3 mr-1" />Muta tutti
              </Button>
            </div>
          )}
        </div>
      )}

      {showTranscription && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-300">Trascrizione Live</span>
              {isTranscribing && (
                <span className="flex items-center gap-1 text-[10px] bg-red-600/20 text-red-400 px-1.5 py-0.5 rounded-full border border-red-600/30">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />LIVE
                </span>
              )}
              {!isTranscribing && (
                <span className="text-[10px] bg-gray-700/50 text-gray-400 px-1.5 py-0.5 rounded-full">OFF</span>
              )}
            </div>
            {isHost && (
              <Button
                variant="unstyled"
                size="sm"
                className={`h-6 text-[10px] px-2 ${isTranscribing ? 'bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/30' : 'bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/30'}`}
                onClick={toggleTranscription}
              >
                {isTranscribing ? 'Ferma' : 'Avvia'}
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
            {transcriptionLines.length === 0 && (
              <div className="text-center mt-8 space-y-2">
                <FileText className="w-8 h-8 text-gray-600 mx-auto" />
                <p className="text-gray-500 text-xs">
                  {isTranscribing ? 'In attesa di audio...' : isHost ? 'Avvia la trascrizione per iniziare' : 'La trascrizione non è ancora attiva'}
                </p>
              </div>
            )}
            {transcriptionLines.map((line, i) => (
              <div key={i} className="text-xs">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">
                    {line.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span className={`font-medium flex-shrink-0 ${getSpeakerColor(line.speakerIdentity)}`}>
                    {line.speakerName}:
                  </span>
                  <span className="text-gray-200">{line.text}</span>
                </div>
              </div>
            ))}
            <div ref={transcriptionEndRef} />
          </div>
          <div className="px-3 py-1.5 border-t border-gray-800 flex items-center justify-between">
            <span className="text-[10px] text-gray-500">{transcriptionLines.length} segmenti</span>
            {transcriptionLines.length > 0 && (
              <button
                className="text-[10px] text-indigo-400 hover:text-indigo-300"
                onClick={() => {
                  const text = transcriptionLines.map(l =>
                    `[${l.timestamp.toLocaleTimeString('it-IT')}] ${l.speakerName}: ${l.text}`
                  ).join('\n');
                  navigator.clipboard.writeText(text);
                  toast({ title: 'Trascrizione copiata!' });
                }}
              >
                <Copy className="w-3 h-3 inline mr-0.5" />Copia tutto
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div ref={containerRef} className="flex flex-col h-screen bg-gray-950 text-white">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-gray-900/80 border-b border-gray-800">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Circle className="w-3 h-3 text-red-500 fill-red-500 animate-pulse flex-shrink-0" />
          <span className="font-medium text-xs sm:text-sm truncate">{roomInfo?.title || 'Video Call'}</span>
          <span className="text-gray-400 text-xs sm:text-sm font-mono flex-shrink-0">{formatDuration(callDuration)}</span>
          {recordingActive && (
            <span
              className="inline-flex items-center gap-1 bg-red-600/90 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full animate-pulse flex-shrink-0"
              title="Registrazione in corso"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
              REC
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {isHost && pendingParticipants.length > 0 && (
            <span className="text-[10px] sm:text-xs bg-amber-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full animate-pulse">{pendingParticipants.length} in attesa</span>
          )}
          <span className="text-[10px] sm:text-xs text-gray-400">{totalCount} part.</span>
          <button
            onClick={() => setViewMode(prev => prev === 'gallery' ? 'speaker' : 'gallery')}
            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            title={viewMode === 'gallery' ? 'Vista speaker' : 'Vista galleria'}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors hidden sm:block"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        {isSpeakerLayout ? (
          <div className="flex-1 flex flex-col p-2 sm:p-3 gap-2 sm:gap-3 min-w-0">
            <div className="flex-1 relative bg-gray-900 rounded-xl overflow-hidden min-h-0">
              {screenShareActive ? (
                screenShareActive === myIdentityRef.current ? (
                  <div className="w-full h-full flex items-center justify-center">
                    {screenTrack ? (
                      <LocalScreenView track={screenTrack} />
                    ) : (
                      <p className="text-gray-400">Condivisione schermo in corso...</p>
                    )}
                  </div>
                ) : (
                  <RemoteVideoTile identity={screenShareActive} remoteTracks={remoteTracks} isScreenTrack />
                )
              ) : focusedParticipant && focusedParticipant !== myIdentityRef.current ? (
                <div className="w-full h-full relative group">
                  <RemoteVideoTile identity={focusedParticipant} remoteTracks={remoteTracks} />
                  <div className="absolute bottom-2 left-2 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
                    {resolveDisplayName(focusedParticipant)}
                  </div>
                </div>
              ) : (
                <VideoTile info={null} isLocal isFocused />
              )}
            </div>

            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 flex-shrink-0" style={{ minHeight: '100px', maxHeight: '140px' }}>
              {(focusedParticipant !== myIdentityRef.current || screenShareActive) && (
                <div className="w-[140px] sm:w-[180px] flex-shrink-0 group relative bg-gray-900 rounded-xl overflow-hidden">
                  <div ref={localVideoRef} className="w-full h-full" />
                  {!isVideoEnabled && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                      <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-sm font-bold text-white">Tu</div>
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1 z-10 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                    {!isAudioEnabled && <MicOff className="w-2.5 h-2.5 text-red-400 inline mr-0.5" />}Tu
                  </div>
                </div>
              )}
              {participantArray.filter(p => p.identity !== focusedParticipant || screenShareActive).map(p => (
                <div
                  key={p.identity}
                  className={`w-[140px] sm:w-[180px] flex-shrink-0 group relative bg-gray-900 rounded-xl overflow-hidden cursor-pointer ${
                    dominantSpeaker === p.identity ? 'ring-2 ring-indigo-500' : ''
                  }`}
                  onClick={() => setPinnedParticipant(prev => prev === p.identity ? null : p.identity)}
                >
                  {p.videoEnabled ? (
                    <RemoteVideoTile identity={p.identity} remoteTracks={remoteTracks} />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold text-white">
                        {p.displayName.slice(0, 2).toUpperCase()}
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1 z-10 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded truncate max-w-[120px]">
                    {!p.audioEnabled && <MicOff className="w-2.5 h-2.5 text-red-400 inline mr-0.5" />}
                    {p.displayName}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={`flex-1 p-2 sm:p-3 grid gap-2 sm:gap-3 auto-rows-fr ${getGalleryGridClass()}`}>
            <div className={`relative bg-gray-900 rounded-xl overflow-hidden group ${dominantSpeaker === null ? 'ring-2 ring-indigo-500' : ''}`}>
              <div ref={localVideoRef} className="w-full h-full" />
              {!isVideoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-indigo-600 flex items-center justify-center text-lg sm:text-2xl font-bold">Tu</div>
                </div>
              )}
              <div className="absolute bottom-1.5 left-1.5 z-10 flex items-center gap-1 bg-black/60 text-white text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg">
                {!isAudioEnabled && <MicOff className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-400" />}
                <span>Tu</span>
              </div>
            </div>

            {participantArray.map(p => (
              <div
                key={p.identity}
                className={`relative bg-gray-900 rounded-xl overflow-hidden group cursor-pointer ${
                  dominantSpeaker === p.identity ? 'ring-2 ring-indigo-500' : ''
                }`}
                onClick={() => { setPinnedParticipant(p.identity); }}
              >
                {p.videoEnabled ? (
                  <RemoteVideoTile identity={p.identity} remoteTracks={remoteTracks} />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-700 flex items-center justify-center text-lg sm:text-2xl font-bold">
                      {p.displayName.slice(0, 2).toUpperCase()}
                    </div>
                  </div>
                )}
                <div className="absolute bottom-1.5 left-1.5 z-10 flex items-center gap-1 bg-black/60 text-white text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg">
                  {!p.audioEnabled && <MicOff className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-400" />}
                  {getNetworkQualityIcon(p.networkQuality)}
                  <span className="truncate max-w-[80px] sm:max-w-[120px]">{p.displayName}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setPinnedParticipant(prev => prev === p.identity ? null : p.identity); }}
                  className="absolute top-1.5 right-1.5 z-10 bg-black/40 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Pin className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {sidebarOpen && (
          <>
            <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => { setShowChat(false); setShowParticipants(false); setShowTranscription(false); }} />
            <div className={`
              fixed md:relative right-0 top-0 bottom-0 z-50 md:z-auto
              w-[85vw] sm:w-80 md:w-80
              bg-gray-900 md:bg-gray-900/80 border-l border-gray-800 flex flex-col
              transition-transform md:transition-none
            `}>
              <div className="md:hidden flex items-center justify-between px-3 py-2 border-b border-gray-800">
                <span className="text-sm font-medium">{showChat ? 'Chat' : showTranscription ? 'Trascrizione' : 'Partecipanti'}</span>
                <button onClick={() => { setShowChat(false); setShowParticipants(false); setShowTranscription(false); }} className="p-1 rounded hover:bg-gray-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {sidePanelContent}
            </div>
          </>
        )}
      </div>

      {/* Audio remoto: hidden <audio> renderizzati declarativamente — uno per
          partecipante che pubblica audio. Sostituisce `document.body.appendChild`
          imperativo. Key=identity garantisce stabilità del DOM node. */}
      {Array.from(remoteTracks.entries()).map(([identity, bundle]) => (
        bundle.audio ? <RemoteAudioView key={`audio-${identity}`} track={bundle.audio} /> : null
      ))}

      <div className="flex items-center justify-center gap-1.5 sm:gap-3 px-2 sm:px-4 py-2 sm:py-3 bg-gray-900/80 border-t border-gray-800 flex-wrap">
        <Button variant="unstyled" size="sm"
          className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 ${isAudioEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500' : 'bg-red-600 hover:bg-red-700 text-white'}`}
          onClick={toggleAudio}
          title={isAudioEnabled ? 'Disattiva microfono' : 'Attiva microfono'}
        >
          {isAudioEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
        </Button>

        <Button variant="unstyled" size="sm"
          className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 ${isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500' : 'bg-red-600 hover:bg-red-700 text-white'}`}
          onClick={toggleVideo}
          title={isVideoEnabled ? 'Disattiva camera' : 'Attiva camera'}
        >
          {isVideoEnabled ? <VideoIcon className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
        </Button>

        {screenShareSupported && (
          <Button variant="unstyled" size="sm"
            className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 ${isScreenSharing ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500'}`}
            onClick={toggleScreenShare}
            title="Condividi schermo"
          >
            {isScreenSharing ? <MonitorOff className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
          </Button>
        )}

        <div className="w-px h-6 sm:h-8 bg-gray-700 mx-0.5 sm:mx-1 hidden sm:block" />

        <Button variant="unstyled" size="sm"
          className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 ${showChat ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500'}`}
          onClick={() => { setShowChat(!showChat); setShowParticipants(false); setShowTranscription(false); setShowMobilePanel(false); }}
          title="Chat"
        >
          <MessageSquare className="w-4 h-4" />
        </Button>

        <Button variant="unstyled" size="sm"
          className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 ${showParticipants ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500'}`}
          onClick={() => { setShowParticipants(!showParticipants); setShowChat(false); setShowTranscription(false); setShowMobilePanel(false); }}
          title="Partecipanti"
        >
          <Users className="w-4 h-4" />
        </Button>

        <Button variant="unstyled" size="sm"
          className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 relative ${showTranscription ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500'}`}
          onClick={() => { setShowTranscription(!showTranscription); setShowChat(false); setShowParticipants(false); setShowMobilePanel(false); }}
          title="Trascrizione"
        >
          <FileText className="w-4 h-4" />
          {isTranscribing && (
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-900 animate-pulse" />
          )}
        </Button>

        {isHost && (
          <Button variant="unstyled" size="sm"
            className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 relative ${recordingActive ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-500'} ${recordingBusy ? 'opacity-60 cursor-wait' : ''}`}
            onClick={recordingActive ? stopRecording : startRecording}
            disabled={recordingBusy}
            title={recordingActive ? 'Ferma registrazione' : 'Avvia registrazione'}
          >
            {recordingBusy
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Circle className={`w-4 h-4 ${recordingActive ? 'fill-white' : ''}`} />}
          </Button>
        )}

        {!guestToken && (
          <Button variant="unstyled" size="sm"
            className="rounded-full w-10 h-10 sm:w-11 sm:h-11 bg-gray-700 hover:bg-gray-600 text-white border border-gray-500 hidden sm:flex"
            onClick={generateGuestLink}
            title="Link ospite"
          >
            <Link className="w-4 h-4" />
          </Button>
        )}

        <div className="relative sm:hidden">
          <Button variant="unstyled" size="sm"
            className="rounded-full w-10 h-10 bg-gray-700 hover:bg-gray-600 text-white border border-gray-500"
            onClick={() => setShowMoreMenu(!showMoreMenu)}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
          {showMoreMenu && (
            <div className="absolute bottom-12 right-0 bg-gray-800 border border-gray-700 rounded-xl shadow-xl py-1.5 min-w-[160px] z-50">
              {screenShareSupported && (
                <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-700 flex items-center gap-2" onClick={() => { toggleScreenShare(); setShowMoreMenu(false); }}>
                  <Monitor className="w-3.5 h-3.5" />{isScreenSharing ? 'Ferma condivisione' : 'Condividi schermo'}
                </button>
              )}
              {!guestToken && (
                <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-700 flex items-center gap-2" onClick={() => { generateGuestLink(); setShowMoreMenu(false); }}>
                  <Link className="w-3.5 h-3.5" />Link ospite
                </button>
              )}
              <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-700 flex items-center gap-2" onClick={() => { toggleFullscreen(); setShowMoreMenu(false); }}>
                <Maximize2 className="w-3.5 h-3.5" />Fullscreen
              </button>
              <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-700 flex items-center gap-2" onClick={() => { setViewMode(prev => prev === 'gallery' ? 'speaker' : 'gallery'); setShowMoreMenu(false); }}>
                <LayoutGrid className="w-3.5 h-3.5" />{viewMode === 'gallery' ? 'Vista speaker' : 'Vista galleria'}
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-6 sm:h-8 bg-gray-700 mx-0.5 sm:mx-1" />

        <Button variant="unstyled" size="sm"
          className="rounded-full px-3 sm:px-5 h-10 sm:h-11 bg-red-600 hover:bg-red-700 text-white"
          onClick={leaveRoom}
        >
          <PhoneOff className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Esci</span>
        </Button>

        {isHost && (
          <Button variant="unstyled" size="sm"
            className="rounded-full px-3 sm:px-4 h-10 sm:h-11 bg-red-800 hover:bg-red-900 text-white hidden sm:flex"
            onClick={endRoomForAll}
            title="Termina per tutti"
          >
            <PhoneOff className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Termina</span>
          </Button>
        )}
      </div>

      {showGuestLinkModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Link ospite</h3>
              <Button variant="unstyled" size="sm" onClick={() => setShowGuestLinkModal(false)} className="bg-transparent text-gray-400 hover:text-white hover:bg-white/10">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-400 mb-3">Condividi questo link con persone esterne per farle partecipare alla call:</p>
            <div className="flex gap-2">
              <Input value={guestLink} readOnly className="text-xs bg-gray-800 border-gray-700 text-white" />
              <Button variant="unstyled" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { navigator.clipboard.writeText(guestLink); toast({ title: 'Link copiato!' }); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {soloWarning && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-lg w-full mx-4">
          <div className="bg-amber-600/95 backdrop-blur-sm text-white px-5 py-3 rounded-xl shadow-2xl border border-amber-500 flex items-center gap-3 animate-in slide-in-from-top">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold">Sei l'unico partecipante</p>
              <p className="text-xs opacity-90">
                La sessione verrà chiusa tra{' '}
                <span className="font-mono font-bold text-base">
                  {Math.floor(soloWarning.countdown / 60)}:{String(soloWarning.countdown % 60).padStart(2, '0')}
                </span>
              </p>
            </div>
            <Button
              variant="unstyled"
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded-lg"
              onClick={() => {
                if (soloCountdownRef.current) clearInterval(soloCountdownRef.current);
                setSoloWarning(null);
                if (roomRef.current) roomRef.current.disconnect();
                setStatus('ended');
              }}
            >
              Esci ora
            </Button>
          </div>
        </div>
      )}

      {muteRequestDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl p-6 max-w-sm w-full mx-4 border border-gray-700 text-center space-y-4">
            <VolumeX className="w-12 h-12 text-amber-400 mx-auto" />
            <h3 className="font-semibold text-lg">{muteRequestDialog} ti chiede di mutare il microfono</h3>
            <div className="flex gap-3 justify-center">
              <Button variant="unstyled" size="sm" className="bg-transparent border border-gray-600 text-gray-300 hover:bg-gray-800 px-4" onClick={() => setMuteRequestDialog(null)}>
                Ignora
              </Button>
              <Button variant="unstyled" size="sm" className="bg-amber-600 hover:bg-amber-700 text-white px-4" onClick={() => {
                if (localAudioTrack && isAudioEnabled) {
                  localAudioTrack.disable();
                  setIsAudioEnabled(false);
                }
                setMuteRequestDialog(null);
              }}>
                Muta microfono
              </Button>
            </div>
          </div>
        </div>
      )}

      {showMoreMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
      )}
    </div>
  );
}
